import dayjs from '../../../../shared/services/dayjs.js';
import { convertNumberToEuroGanz } from '../util.js';

/**
 * Drei Erfüllungszustände, überall identische Bedeutung — siehe BEDARFSPLANER.md §0.
 * @param {number} filled
 * @param {number} need
 * @returns {'full'|'part'|'empty'}
 */
function stateCls(filled, need) {
	if (filled >= need) return 'full';
	if (filled === 0) return 'empty';
	return 'part';
}

/**
 * Kurzformat einer Uhrzeit: volle Stunde ohne Minuten (18), sonst mit (18:30).
 * @param {Date|string} datum
 * @returns {string}
 */
function kurzeZeit(datum) {
	const d = dayjs(datum);
	return d.minute() === 0 ? d.format('H') : d.format('H:mm');
}

/**
 * Gruppiert die flache Liste der Bedarfszeilen (vom Server, eine Zeile pro Posten x Tag x Zeitfenster)
 * zu Tag -> Bereich. Jede Bedarfszeile bleibt ein eigener Eintrag (eigener Chip) — auch mehrere Zeitfenster
 * desselben Postens am Tag werden nicht zusammengefasst. Innerhalb des Bereichs nach Posten, dann Beginn
 * sortiert. Bereich-Rollups (need/filled) sind IMMER abgeleitete Summen über ihre Zeilen — siehe
 * BEDARFSPLANER.md §0/§2.3. `geplant` (AG-Kosten, nur bei Budget-Rollen vom Server) wird ebenso aufsummiert.
 * @param {array<object>} bedarfZeilen Server-Antwort von bedarfService.holeBedarfZeitraum
 * @returns {Map<string, Map<string, {bereichID:string, bereich:string, need:number, filled:number, geplant:number, zeilen: array}>>}
 *   Tag (YYYY-MM-DD) -> BereichID -> Bereichs-Gruppe mit ihren Bedarfszeilen
 */
function groupiereBedarf(bedarfZeilen) {
	const tage = new Map();
	bedarfZeilen.forEach((zeile) => {
		const tag = dayjs(zeile.Datum).format('YYYY-MM-DD');
		if (!tage.has(tag)) {
			tage.set(tag, new Map());
		}
		const bereiche = tage.get(tag);
		if (!bereiche.has(zeile.BereichID)) {
			bereiche.set(zeile.BereichID, {
				bereichID: zeile.BereichID,
				bereich: zeile.Bereich,
				need: 0,
				filled: 0,
				geplant: 0,
				zeilen: [],
			});
		}
		const bereichsGruppe = bereiche.get(zeile.BereichID);
		bereichsGruppe.zeilen.push({
			bedarfID: zeile._id,
			postenID: zeile.PostenID,
			posten: zeile.Posten,
			need: zeile.BedarfAnzahl,
			filled: zeile.filled,
			geplant: zeile.geplant || 0,
			von: zeile.SchichtVon,
			zeit: `${kurzeZeit(zeile.SchichtVon)}-${kurzeZeit(zeile.SchichtBis)}`,
		});
		bereichsGruppe.need += zeile.BedarfAnzahl;
		bereichsGruppe.filled += zeile.filled;
		bereichsGruppe.geplant += zeile.geplant || 0;
	});
	tage.forEach((bereiche) => bereiche.forEach((gruppe) => {
		gruppe.zeilen.sort((a, b) => (a.posten || '').localeCompare(b.posten || '') || new Date(a.von) - new Date(b.von));
	}));
	return tage;
}

/**
 * Füllt einen Zähler (Chip, Posten- oder Event-Zeile) mit beiden Werten: Besetzung (filled/need) und geplante
 * AG-Kosten (ganze Euro, "—" ohne Kosten). Welcher sichtbar ist, entscheidet allein die Klasse `.ko-modus` am
 * Bedarfsplaner (CSS) — der Besetzung/Kosten-Umschalter braucht so kein Neu-Rendern. Siehe KOSTENANZEIGE.md §4.
 * @param {HTMLElement} el
 * @param {number} filled
 * @param {number} need
 * @param {number} [geplant] nur bei Rollen mit Budget-Einsicht vom Server geliefert
 */
function fuelleZaehler(el, filled, need, geplant) {
	const besetzung = document.createElement('span');
	besetzung.className = 'v-bes';
	besetzung.textContent = `${filled}/${need}`;
	const kosten = document.createElement('span');
	kosten.className = 'v-ko';
	kosten.textContent = geplant > 0 ? convertNumberToEuroGanz(geplant) : '—';
	el.replaceChildren(besetzung, kosten);
}

export { stateCls, groupiereBedarf, fuelleZaehler };
